import Phaser from 'phaser';
import { DEPTHS } from '../util/constants.js';

const MAX_LIFETIME_MS = 2200;

/**
 * Pooled projectile group. Projectiles are pre-allocated and recycled via
 * group.get()/killAndHide — no runtime instantiate/destroy during play.
 */
export class ProjectilePool {
  constructor(scene, { textureKey, poolSize = 40, ownerTeam }) {
    this.scene = scene;
    this.textureKey = textureKey;
    this.ownerTeam = ownerTeam; // 'player' | 'enemy'

    this.group = scene.physics.add.group({
      classType: Phaser.Physics.Arcade.Image,
      maxSize: poolSize,
      runChildUpdate: false
    });

    // Native frame size of the beam texture (always drawn wide-and-short,
    // horizontal) — used in fire() to size the Arcade body correctly for
    // vertical shots too. Read once here rather than from the frame on
    // every shot.
    const src = scene.textures.get(textureKey).getSourceImage();
    this.frameW = src.width;
    this.frameH = src.height;

    for (let i = 0; i < poolSize; i++) {
      const proj = this.group.create(-1000, -1000, textureKey);
      proj.setActive(false);
      proj.setVisible(false);
      // Without an explicit depth, these default to 0 — same as the far
      // background layer, and *below* the near parallax layer (depth 1),
      // whose opaque building silhouettes were hiding most of every
      // horizontal shot behind them.
      proj.setDepth(DEPTHS.PROJECTILES);
      if (proj.body) {
        proj.body.enable = false;
        // Without this, the world's gravity pulls every shot into a falling
        // arc instead of a straight line — a "laser" the player is meant to
        // dodge by jumping over needs to travel in a straight, physics-free
        // line, not curve into them.
        proj.body.setAllowGravity(false);
      }
      proj.setData('birth', 0);
      proj.setData('damage', 1);
      proj.setData('team', ownerTeam);
    }
  }

  fire(x, y, velocityX, velocityY, { damage = 1, angleLock = true } = {}) {
    /** @type {Phaser.Physics.Arcade.Image} */
    const proj = this.group.getFirstDead(false);
    if (!proj) return null;

    proj.setPosition(x, y);
    proj.setActive(true);
    proj.setVisible(true);
    if (proj.body) {
      proj.body.enable = true;
      // Arcade bodies don't rotate with the sprite — angleLock only spins
      // the *visual* frame. A vertical shot (fired straight up/down at a
      // target above/below) rotates the wide-short beam texture 90° on
      // screen, but without this the collision box would stay wide-short
      // too: a horizontal footprint sitting across a vertical beam, so it
      // could clip a target standing beside the beam that never actually
      // touches it, or miss one directly in the beam's path. Swapping the
      // body's width/height for vertical shots keeps the (unrotated) box
      // matching what's actually drawn.
      const vertical = angleLock && Math.abs(velocityY) > Math.abs(velocityX);
      proj.body.setSize(vertical ? this.frameH : this.frameW, vertical ? this.frameW : this.frameH);
      proj.body.reset(x, y);
    }
    proj.setVelocity(velocityX, velocityY);
    if (angleLock) proj.setRotation(Math.atan2(velocityY, velocityX));
    proj.setData('birth', this.scene.time.now);
    proj.setData('damage', damage);
    proj.setData('team', this.ownerTeam);
    return proj;
  }

  recycle(proj) {
    proj.setActive(false);
    proj.setVisible(false);
    proj.setVelocity(0, 0);
    if (proj.body) proj.body.enable = false;
    proj.setPosition(-1000, -1000);
  }

  /** Call once per frame from the owning scene's update loop. */
  update(now, worldBounds) {
    this.group.children.iterate((proj) => {
      if (!proj || !proj.active) return;
      const age = now - proj.getData('birth');
      const outOfBounds =
        proj.x < worldBounds.x - 64 ||
        proj.x > worldBounds.x + worldBounds.width + 64 ||
        proj.y < worldBounds.y - 64 ||
        proj.y > worldBounds.y + worldBounds.height + 64;
      if (age > MAX_LIFETIME_MS || outOfBounds) {
        this.recycle(proj);
      }
    });
  }
}
